import React, { useCallback, useState, useMemo } from 'react';
import { Typography, Card, CardContent, Switch, Tooltip, Box, Chip } from '@mui/material';
import { Percent } from '@mui/icons-material';
import { ResponsiveBar } from '@nivo/bar';
import { useTheme } from '@mui/material/styles';
import { useChartData } from './useChartData';
import { CARD_TYPES, CHART_VIEWS } from './ItemAnalytics';

function UsageChartPicklist({ timeSeriesData, period, itemData, dateRange, selectedPeriod, setSelectedPeriod }) {
    const theme = useTheme();
    const [normalizeByCase, setNormalizeByCase] = useState(false);

    // Use the shared chart data hook
    const rawChartData = useChartData(timeSeriesData, CHART_VIEWS.PICKLIST_VIEW, period, dateRange);

    // Process chart data to optionally normalize by case count
    const chartData = useMemo(() => {
        if (!rawChartData || !normalizeByCase) return rawChartData;

        return rawChartData.map(item => {
            const cases = item.cases || 0;
            if (cases === 0) return item;
            
            return {
                ...item,
                run: item.run / cases,
                restock: item.restock / cases,
                'open not used': item['open not used'] / cases,
                originalRun: item.run,
                originalRestock: item.restock,
                originalOpenNotUsed: item['open not used']
            };
        });
    }, [rawChartData, normalizeByCase]);

    // Chart colors for picklist view - use theme colors
    const chartColors = [
        theme.palette.analytics.cardTypes.run,
        theme.palette.analytics.cardTypes.restock,
        theme.palette.analytics.cardTypes.openNotUsed,
    ];

    // Handle period selection
    const handlePeriodClick = (data) => {
        const periodId = data.indexValue;
        setSelectedPeriod(selectedPeriod === periodId ? null : periodId);
    };

    // Custom color function to highlight selected period
    const getBarColor = useCallback((bar) => {
        const isSelected = selectedPeriod === bar.indexValue;
        const baseColorIndex = [CARD_TYPES.RUN, CARD_TYPES.RESTOCK, CARD_TYPES.OPEN_NOT_USED].indexOf(bar.id);
        const baseColor = chartColors[baseColorIndex];
        
        if (isSelected) {
            return baseColor; // Keep full color for selected
        } else if (selectedPeriod) {
            return `${baseColor}80`; // Add transparency for unselected when something is selected
        }
        return baseColor; // Default color when nothing is selected
    }, [selectedPeriod, chartColors]);

    // Memoized format period label for x-axis
    const formatPeriodLabel = useCallback((periodStr) => {
        if (period === 'day') {
            // Parse as local date to avoid timezone issues
            const [year, month, day] = periodStr.split('-').map(Number);
            const date = new Date(year, month - 1, day); // month is 0-indexed
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        } else if (period === 'week') {
            return `Week ${periodStr.split('-W')[1] || periodStr}`;
        } else if (period === 'month') {
            const [year, month] = periodStr.split('-').map(Number);
            const date = new Date(year, month - 1);
            return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        } else if (period === 'quarter') {
            const [year, quarter] = periodStr.split('-Q');
            return `Q${quarter} ${year}`;
        }
        return periodStr;
    }, [period]);

    if (!chartData.length) {
        return (
            <Card>
                <CardContent>
                    <Typography color="text.secondary" align="center">
                        No usage data available for the selected time period
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <div>
                        <Typography variant="h6" gutterBottom>
                            {'Case picking deviations from usage in quantity over time'}
                            {normalizeByCase ? ' (per case)' : ''}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            This chart shows the quantity or cost of items that were picked open but not used, restocked, or run for in the cases occuring within the selected time period.<br/>
                            If any of these values are high, it may indicate inefficiencies in inventory management or case picking processes.
                        </Typography>
                    </div>
                    <Tooltip title="Normalize per case">
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Percent sx={{ fontSize: '1rem', opacity: normalizeByCase ? 1 : 0.5, mr: 0.5 }} />
                            <Switch 
                                checked={normalizeByCase}
                                onChange={(e) => setNormalizeByCase(e.target.checked)}
                                size="small"
                            />
                        </Box>
                    </Tooltip>
                </Box>
                <div style={{ height: 400, marginTop: 36 }}>
                    <ResponsiveBar
                        key={`bar-${timeSeriesData?.length || 0}-${period}-${dateRange?.startDate}-${dateRange?.endDate}`}
                        data={chartData}
                        keys={['run', 'restock', 'open not used']}
                        indexBy="period"
                        margin={{ top: 50, right: 130, bottom: 110, left: 80 }}
                        padding={0.3}
                        groupMode="grouped"
                        valueScale={{ type: 'linear' }}
                        indexScale={{ type: 'band', round: true }}
                        colors={getBarColor}
                        onClick={handlePeriodClick}
                        borderColor={{
                            from: 'color',
                            modifiers: [
                                [
                                    'darker',
                                    1.6
                                ]
                            ]
                        }}
                        axisTop={null}
                        axisRight={null}
                        axisBottom={{
                            tickSize: 5,
                            tickPadding: 12,
                            tickRotation: -45,
                            legend: 'Time Period',
                            legendPosition: 'middle',
                            legendOffset: 80,
                            format: formatPeriodLabel
                        }}
                        axisLeft={{
                            tickSize: 5,
                            tickPadding: 5,
                            tickRotation: 0,
                            legend: normalizeByCase ? 'Quantity per Case' : 'Quantity',
                            legendPosition: 'middle',
                            legendOffset: -60
                        }}
                        enableLabel={false}
                        labelSkipWidth={12}
                        labelSkipHeight={12}
                        labelTextColor={{
                            from: 'color',
                            modifiers: [
                                [
                                    'darker',
                                    1.6
                                ]
                            ]
                        }}
                        legends={[
                            {
                                dataFrom: 'keys',
                                anchor: 'bottom-right',
                                direction: 'column',
                                justify: false,
                                translateX: 120,
                                translateY: 0,
                                itemsSpacing: 2,
                                itemWidth: 100,
                                itemHeight: 20,
                                itemDirection: 'left-to-right',
                                itemOpacity: 0.85,
                                symbolSize: 20,
                                effects: [
                                    {
                                        on: 'hover',
                                        style: {
                                            itemOpacity: 1
                                        }
                                    }
                                ]
                            }
                        ]}
                        theme={{
                            background: 'transparent',
                            text: {
                                fill: theme.palette.text.primary
                            },
                            axis: {
                                ticks: {
                                    line: {
                                        stroke: theme.palette.divider
                                    },
                                    text: {
                                        fill: theme.palette.text.primary
                                    }
                                },
                                legend: {
                                    text: {
                                        fill: theme.palette.text.primary
                                    }
                                }
                            },
                            grid: {
                                line: {
                                    stroke: theme.palette.divider,
                                    strokeWidth: 1
                                }
                            }
                        }}
                    />
                </div>
                {selectedPeriod && (
                    <Box sx={{ mt: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1, display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                            Selected Period: {formatPeriodLabel(selectedPeriod)}
                        </Typography>
                        <Chip 
                            label="Clear Selection" 
                            size="small" 
                            variant="outlined"
                            onClick={() => setSelectedPeriod(null)}
                            sx={{ ml: 2 }}
                        />
                    </Box>
                )}
            </CardContent>
        </Card>
    );
}

export default UsageChartPicklist;